import React, { useState, useCallback } from "react";

import moment from "moment";
import queryString from "query-string";
import { eachMonthOfInterval } from "date-fns";

import ReportService from "../../../services/reportService";

import { generateCSV, downloadEmployeeSummaryByDepartmentCSV } from "../utils";

import { formatDate } from "../../../helpers/date";

import Filters from "./EmployeeUtilizationFilters";

import EmployeeUtilizationList from "./EmployeeUtilizationList";

import "./EmployeeUtilizationPage.scss";

import {
  getEmploymentStatusLabel,
  calcDepartmentUtilizationRate,
  calcEmployeeUtilizationRate
} from "./utils";

const EmployeeUtilizationPage = () => {
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    startDate: formatDate(moment().startOf("month").toDate(), "yyyy-MM-dd"),
    endDate: formatDate(new Date(), "yyyy-MM-dd")
  });
  const [data, setData] = useState([]);
  const [monthColumns, setMonthColumns] = useState([]);

  const fetchData = useCallback(async () => {
    setLoading(true);

    const months = eachMonthOfInterval({
      start: new Date(moment(filters.startDate).startOf("day")),
      end: new Date(moment(filters.endDate).endOf("day"))
    }).map((m) => moment(m).format("M-YYYY"));
    setMonthColumns(months);

    setData([]);
    const query = queryString.stringify(
      {
        ...filters,
        startDate: moment(filters.startDate)
          .startOf("day")
          .format("YYYY-MM-DD HH:mm:ss"),
        endDate: moment(filters.endDate)
          .endOf("day")
          .format("YYYY-MM-DD HH:mm:ss")
      },
      { arrayFormat: "bracket" }
    );
    const result = await ReportService.get(`/employee-utilization?${query}`);
    setData(result);
    setLoading(false);
  }, [filters]);

  const handleChange = (name) => (value) => {
    setLoading(false);
    if (
      Array.isArray(value) &&
      (name === "departments" ||
        name === "customers" ||
        name === "managers" ||
        name === "projects")
    ) {
      setFilters((prev) => ({ ...prev, [name]: value.map((v) => v.value) }));
    } else {
      setFilters((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleResetClick = () => {
    setFilters({
      startDate: formatDate(moment().startOf("month").toDate(), "yyyy-MM-dd"),
      endDate: formatDate(new Date(), "yyyy-MM-dd")
    });
    setData([]);
  };

  const generateDataToExport = () => {
    const result = data.map((department) => {
      const objectToReturn = {
        header: ["Employee", ...monthColumns?.map((month) => month)],
        body: [],
        footer: []
      };
      department.employees.forEach((employee) => {
        const employeeData = monthColumns.map((month) => {
          const employeeMonth = employee.months.find(
            (m) => `${m.month}-${m.year}` === month
          );

          if (employeeMonth) {
            return {
              [month]: calcEmployeeUtilizationRate(employeeMonth)
            };
          }
          return { [month]: "--" };
        });

        const employeeDataObject = Object.assign({}, ...employeeData);

        const userInformation = {
          Employee: `${employee.employeeName} ${getEmploymentStatusLabel(
            employee.employmentStatus
          )}`,
          ...employeeDataObject
        };
        objectToReturn.body.push(userInformation);
      });

      const departmentData = monthColumns.map((month) => ({
        [month]: calcDepartmentUtilizationRate(department, month)
      }));

      const departmentDataObject = Object.assign({}, ...departmentData);

      const totals = {
        Employee: `Full Department`,
        ...departmentDataObject
      };

      objectToReturn.body.push(totals);

      return objectToReturn;
    });
    return result;
  };

  const handleExport = async () => {
    const information = generateDataToExport();
    const csvData = information.map((info) =>
      generateCSV(info.header, [...info.body, ...[info.footer]], `\n\n`)
    );

    const title = `Employee Utilization from ${moment(filters.startDate).format(
      "MM/DD/YYYY"
    )} to ${moment(filters.endDate).format("MM/DD/YYYY")}`;

    downloadEmployeeSummaryByDepartmentCSV(
      `EmployeeUtilization-${formatDate(moment(), "yyyyMMdd")}`,
      csvData,
      title
    );
  };

  return (
    <div id="employee-utilization-page" className="report-page">
      <Filters
        filters={filters}
        onChange={handleChange}
        onFetchClick={fetchData}
        onResetClick={handleResetClick}
        loading={loading}
        onCSVExport={handleExport}
        exportDisabled={!data?.length}
        disabled={
          loading ||
          !Object.keys(filters).length ||
          !filters.startDate ||
          !filters.endDate ||
          !moment(filters.startDate).isBefore(filters.endDate)
        }
      />
      <div style={{ textAlign: "center", fontSize: "2rem" }}>
        Billable Hrs / (Actual Hrs - Personal Hrs)
      </div>
      <EmployeeUtilizationList
        data={data}
        loading={loading}
        monthColumns={monthColumns}
      />
    </div>
  );
};

export default EmployeeUtilizationPage;
